import { Box, Typography, useTheme } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { useCallback, useMemo } from 'react';
import { FieldExtensionComponentProps } from '../../../extensions/types';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

interface StarPickerUiOptions {
  precision?: 0.5 | 1;
  maxValue?: number;
}

export type StarPickerProps = FieldExtensionComponentProps<
  number,
  StarPickerUiOptions
>;

export const StarPicker = ({
  onChange,
  formData,
  schema,
  uiSchema,
  rawErrors,
  required,
  disabled,
  readonly,
  idSchema,
  formContext,
}: StarPickerProps) => {
  const theme = useTheme();
  const effectivePrecision = uiSchema?.['ui:options']?.precision ?? 1;
  const effectiveMaxValue = uiSchema?.['ui:options']?.maxValue ?? 5;
  const effectiveTitle = schema?.title ?? '';

  const scale = Math.max(1, effectiveMaxValue);
  const normalizedValue = formData ? formData / (scale / 5) : 0;

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  const handleChange = useCallback(
    (newValue: number | null) => {
      let value: number | undefined;
      if (newValue === null) {
        value = undefined;
      } else {
        value = newValue * (scale / 5);
      }

      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [onChange, scale, dependencyManager, fieldPath],
  );

  const isDisabled = disabled || readonly;
  const hasErrors = rawErrors?.length > 0;
  let titleColor: string;
  if (hasErrors) {
    titleColor = theme.palette.error.main;
  } else if (isDisabled) {
    titleColor = theme.palette.text.disabled;
  } else {
    titleColor = theme.palette.secondary.main;
  }

  return (
    <Box>
      <Typography
        variant="body2"
        style={{
          fontWeight: 600,
          marginBottom: 4,
          color: titleColor,
        }}
      >
        {effectiveTitle}
        {required && <span aria-hidden>{' *'}</span>}
      </Typography>
      <Box style={{ marginTop: 8, marginBottom: 8 }}>
        <Rating
          value={normalizedValue}
          onChange={(_, newValue) => handleChange(newValue)}
          max={5}
          disabled={disabled}
          precision={effectivePrecision}
          style={{ fontSize: '32px' }}
        />
      </Box>
    </Box>
  );
};
